import type {
  ApprovalDecisionPayload,
  NormalizedEvent,
} from "@/services/types/agent-gateway";
import { MessageSquare } from "lucide-react";
import { AgentActivitySummary } from "./AgentActivitySummary";
import { AgentEvent } from "./AgentEvent";
import { AgentOutgoingMessage } from "./AgentOutgoingMessage";
import { AgentStreamingMessage } from "./AgentStreamingMessage";
import { deduplicateEvents, getParticipatingAgentCount } from "./event-utils";
import {
  type OutgoingMessageAttempt,
  type StreamingMessage,
  buildConversationTimeline,
} from "./message-utils";

type AgentConversationProps = {
  events: NormalizedEvent[];
  sessionActive?: boolean;
  outgoingAttempts?: OutgoingMessageAttempt[];
  streamingMessages?: StreamingMessage[];
  retryDisabled?: boolean;
  onRetry?: (attempt: OutgoingMessageAttempt) => void;
  canApproveVerifiedLeads?: boolean;
  canApproveOutboundActions?: boolean;
  pendingApprovalId?: string | null;
  approvalErrorById?: Record<string, string>;
  onApprovalDecision?: (payload: ApprovalDecisionPayload) => void;
  pendingContinuationId?: string | null;
  onRetryApprovalContinuation?: (approvalId: string) => void;
  answeredInputById?: Record<string, unknown>;
  pendingInputId?: string | null;
  inputErrorById?: Record<string, string>;
  onRequestInputResponse?: (interruptId: string, response: unknown) => void;
};

export function AgentConversation({
  events,
  sessionActive = true,
  outgoingAttempts = [],
  streamingMessages = [],
  retryDisabled = false,
  onRetry,
  canApproveVerifiedLeads = false,
  canApproveOutboundActions = false,
  pendingApprovalId,
  approvalErrorById = {},
  onApprovalDecision,
  pendingContinuationId,
  onRetryApprovalContinuation,
  answeredInputById = {},
  pendingInputId,
  inputErrorById = {},
  onRequestInputResponse,
}: AgentConversationProps) {
  const visibleEvents = deduplicateEvents(events);
  const visibleOutgoingAttempts = outgoingAttempts.filter(
    (attempt) =>
      attempt.requestKind !== "workflow_start" || attempt.status === "failed",
  );
  const timeline = buildConversationTimeline(
    visibleEvents,
    visibleOutgoingAttempts,
  );
  const agentCount = getParticipatingAgentCount(visibleEvents);

  if (
    visibleEvents.length === 0 &&
    visibleOutgoingAttempts.length === 0 &&
    streamingMessages.length === 0
  ) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-slate-200 border-dashed bg-white px-6 py-16 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-brand-lightGreen text-brand-darkGreen">
          <MessageSquare aria-hidden="true" className="size-5" />
        </span>
        <h2 className="mt-4 font-semibold text-lg">No messages yet</h2>
        <p className="mt-1 max-w-sm text-slate-500 text-sm">
          This session has not produced any conversation or activity.
        </p>
      </div>
    );
  }

  return (
    <section aria-label="Session conversation" className="min-w-0">
      <div className="mb-4 flex min-w-0 flex-wrap items-center justify-between gap-4">
        <h2 className="font-semibold text-lg">Conversation</h2>
        <p className="text-right text-slate-500 text-sm">
          {agentCount} participating {agentCount === 1 ? "agent" : "agents"}
        </p>
      </div>

      <div className="space-y-4">
        {timeline.map((item) => {
          if (item.kind === "outgoing") {
            return (
              <AgentOutgoingMessage
                attempt={item.attempt}
                key={`outgoing:${item.attempt.idempotencyKey}`}
                onRetry={onRetry ?? (() => undefined)}
                retryDisabled={retryDisabled}
              />
            );
          }

          if (item.kind === "activity") {
            return (
              <AgentActivitySummary activity={item.activity} key={item.id} />
            );
          }

          const event = item.event;

          return (
            <AgentEvent
              approvalError={
                event.type === "approval_required" &&
                "approval_id" in event.content
                  ? approvalErrorById[event.content.approval_id]
                  : undefined
              }
              answeredInputResponse={
                event.type === "input_required"
                  ? answeredInputById[event.content.interrupt_id]
                  : undefined
              }
              inputAnswered={
                event.type === "input_required"
                  ? Object.prototype.hasOwnProperty.call(
                      answeredInputById,
                      event.content.interrupt_id,
                    )
                  : false
              }
              canApproveOutboundActions={canApproveOutboundActions}
              canApproveVerifiedLeads={canApproveVerifiedLeads}
              event={event}
              key={`event:${event.id}`}
              onApprovalDecision={onApprovalDecision}
              onRetryApprovalContinuation={onRetryApprovalContinuation}
              onRequestInputResponse={onRequestInputResponse}
              pendingApprovalId={pendingApprovalId}
              pendingContinuationId={pendingContinuationId}
              pendingInputId={pendingInputId}
              inputError={
                event.type === "input_required"
                  ? inputErrorById[event.content.interrupt_id]
                  : undefined
              }
              sessionActive={sessionActive}
            />
          );
        })}
        {streamingMessages.map((message) => (
          <AgentStreamingMessage key={message.messageKey} message={message} />
        ))}
      </div>
    </section>
  );
}
